import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import { Link } from "react-router-dom";
import { Github, Search, Smartphone, SlidersHorizontal, Star } from "lucide-react";
import SeoHead from "../components/SeoHead";
import TransparentLogoOldAlex from "../images/transparentLogoOldAlex.png";
import YtLogo from "../images/ytLogo.png";
import { getDirectImageUrl } from "../utils/imageUrls";
import "../styles/oldalexhub.css";

const ANDROID_APPS_ENDPOINT = API_ENDPOINTS.androidApps;

const STATUS_LABEL = { active: "Active", maintenance: "Maintenance", deprecated: "Deprecated" };

const truncateAppText = (text = "", maxWords = 22) => {
  const words = text.split(/\s+/).filter(Boolean);
  return words.length > maxWords ? `${words.slice(0, maxWords).join(" ")}...` : text;
};

const renderAppRating = (rating) => {
  if (rating === null || rating === undefined) {
    return <span className="oldalex-app-rating oldalex-app-rating--empty">Not yet rated</span>;
  }

  const filledStars = Math.round(rating);

  return (
    <span className="oldalex-app-rating">
      <span className="oldalex-app-rating-stars">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            size={13}
            className={`oldalex-star ${i <= filledStars ? "is-filled" : ""}`}
            fill={i <= filledStars ? "currentColor" : "none"}
          />
        ))}
      </span>
      <span className="oldalex-app-rating-value">{Number(rating).toFixed(1)}</span>
    </span>
  );
};

const OldAlexHub = () => {
  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsError, setAppsError] = useState("");
  const [appSearch, setAppSearch] = useState("");
  const [appCategory, setAppCategory] = useState("All");
  const [appSort, setAppSort] = useState("newest");

  useEffect(() => {
    let active = true;
    setAppsLoading(true);
    axios
      .get(ANDROID_APPS_ENDPOINT)
      .then((res) => {
        if (!active) return;
        setApps(res.data.apps || []);
        setAppsError("");
      })
      .catch(() => {
        if (!active) return;
        setAppsError("Couldn't load the app library right now. Please try again shortly.");
      })
      .finally(() => {
        if (active) setAppsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const publishedApps = useMemo(
    () => apps.filter((app) => app.status !== "deprecated"),
    [apps]
  );

  const appCategoryCounts = useMemo(() => {
    return publishedApps.reduce(
      (acc, app) => {
        const cat = app.category || "General";
        acc[cat] = (acc[cat] || 0) + 1;
        return acc;
      },
      { All: publishedApps.length }
    );
  }, [publishedApps]);

  const appCategoryOptions = useMemo(() => {
    const cats = Object.keys(appCategoryCounts)
      .filter((cat) => cat !== "All")
      .sort((a, b) => a.localeCompare(b));
    return ["All", ...cats];
  }, [appCategoryCounts]);

  const filteredApps = useMemo(() => {
    const query = appSearch.trim().toLowerCase();

    const filtered = publishedApps.filter((app) => {
      const inCategory = appCategory === "All" || (app.category || "General") === appCategory;
      if (!inCategory) return false;
      if (!query) return true;

      const haystack = [app.name, app.description, app.category]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });

    const sorted = [...filtered];
    if (appSort === "az") {
      sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (appSort === "rating") {
      sorted.sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1));
    } else {
      sorted.sort(
        (a, b) =>
          new Date(b.releasedAt || b.createdAt || 0) - new Date(a.releasedAt || a.createdAt || 0)
      );
    }
    return sorted;
  }, [publishedApps, appCategory, appSearch, appSort]);

  const appFiltersActive = appCategory !== "All" || appSearch.trim() !== "" || appSort !== "newest";

  const clearAppFilters = () => {
    setAppCategory("All");
    setAppSearch("");
    setAppSort("newest");
  };

  const showAppSection = appsLoading || Boolean(appsError) || publishedApps.length > 0;

  const heroStats = [
    { label: "Founded", value: "2022" },
    { label: "Headquarters", value: "Orlando, FL" },
    { label: "Sectors", value: "Tech Investing, Mobility, Real Estate, AI, SaaS, Media" },
  ];

  return (
    <div className="oldalex-page">
      <SeoHead
        title="Old Alex Hub LLC - Tech Investing, Venture Building, and Portfolio Ownership"
        description="Old Alex Hub LLC is Mohamed Gad's venture-building and investment portfolio across technology stocks, AI, SaaS, media, real estate, TaxiOps, and ownership of Trans Voyage Taxi, a Denver, Colorado based taxi company."
        keywords={[
          "Old Alex Hub LLC",
          "venture builder",
          "technology stock investing",
          "tech investor",
          "TaxiOps",
          "NEMT dispatch software",
          "taxi operations platform",
          "AI operations intelligence",
          "Trans Voyage Taxi",
          "taxi company ownership",
          "Denver taxi company",
        ]}
        canonical="https://www.mohamedgad.com/oldalexhub"
      />
      <section className="container py-5">
        <div className="row align-items-center g-4 oldalex-hero">
          <div className="col-12">
            <p className="eyebrow mb-2">Business Adventures</p>
            <div className="logo-name">
              <img
                src={TransparentLogoOldAlex}
                alt="Old Alex Hub logo"
                className="logo-feature"
              />
              <h1 className="display-5 fw-bold mb-0">Old Alex Hub LLC</h1>
            </div>
            <p className="lead text-muted mb-0">
              A venture-building and investment company connecting disciplined
              tech-industry investing with forward-looking technology,
              including ownership of
              Trans Voyage Taxi, a Denver, Colorado based taxi company.
            </p>
            <div className="oldalex-pills mt-3">
              <span className="pill">Tech investor &amp; venture builder</span>
              <span className="pill">AI + SaaS products</span>
              <span className="pill">TaxiOps</span>
              <span className="pill">Content &amp; media</span>
              <span className="pill">Taxi company ownership</span>
            </div>
            <div className="oldalex-stats mt-3">
              {heroStats.map((item) => (
                <div key={item.label} className="stat-card">
                  <div className="stat-value">{item.value}</div>
                  <div className="stat-label">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="hero-actions mt-3">
              <Link to="/contactus" className="btn btn-primary btn-sm">
                Work with us
              </Link>
              <a
                href="https://www.linkedin.com/in/mohamed-gad-msds-6b286b1b6/"
                className="btn btn-outline-primary btn-sm"
              >
                Connect on LinkedIn
              </a>
              <a
                href="https://github.com/OldAlexhub"
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-dark btn-sm"
              >
                GitHub
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="container pb-5">
        <div className="row g-4">
          <div className="col-lg-4">
            <div className="oldalex-card h-100">
              <p className="eyebrow mb-2">Profile</p>
              <h2 className="h5 fw-bold mb-2">Building and backing ventures</h2>
              <p className="text-muted mb-2">
                Old Alex Hub LLC develops and nurtures a portfolio of businesses
                with a founder-operator mindset, balancing tech-focused capital
                allocation with hands-on product execution across technology,
                media, and operating businesses.
              </p>
              <ul className="text-muted small mb-0 ps-3">
                <li>Founder-owned and led by Mohamed Gad.</li>
                <li>Hybrid model: tech-focused investing, business ownership, and venture building.</li>
                <li>Multi-sector scope to balance risk and growth.</li>
                <li>
                  Ownership includes Trans Voyage Taxi, an operational taxi
                  company based in Denver, Colorado.
                </li>
                <li>
                  Product ventures include TaxiOps, a public SaaS launch for
                  taxi and NEMT operations.
                </li>
              </ul>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="oldalex-card h-100">
              <p className="eyebrow mb-2">Mission</p>
              <h2 className="h5 fw-bold mb-2">
                Invest, build, operate, and ship responsibly
              </h2>
              <p className="text-muted mb-0">
                Drive innovation and durable growth across technology, mobility,
                and media through disciplined investing, pragmatic product
                delivery, and an unwavering commitment to integrity and impact.
              </p>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="oldalex-card h-100">
              <p className="eyebrow mb-2">Vision</p>
              <h2 className="h5 fw-bold mb-2">
                Bridge investing, ownership, and frontier tech
              </h2>
              <p className="text-muted mb-0">
                Lead as a venture-builder that connects technology-focused
                investing and traditional business ownership with emerging
                technologies, shaping businesses in mobility, real estate, AI,
                SaaS, and media to create global impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="container pb-5">
        <div className="oldalex-card">
          <h2 className="h4 fw-bold mb-4">Areas of Expertise</h2>
          <div className="row g-4">
            <div className="col-md-6">
              <div className="oldalex-mini-card h-100">
                <h3 className="h6 fw-bold">Technology Stock Investing</h3>
                <p className="text-muted mb-0">
                  Heavy investing in public technology companies with a strong
                  focus on long-term conviction, sector knowledge, and disciplined
                  capital allocation.
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="oldalex-mini-card h-100">
                <h3 className="h6 fw-bold">Mobility Operations</h3>
                <p className="text-muted mb-0">
                  Operating and supporting transportation businesses with a
                  focus on dispatch, compliance, customer experience, and
                  scalable day-to-day execution.
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="oldalex-mini-card h-100">
                <h3 className="h6 fw-bold">Real Estate Ventures</h3>
                <p className="text-muted mb-0">
                  Multi-market property ownership in the US and Egypt,
                  including Airbnb holdings, that anchors the portfolio with
                  tangible assets and long-term value.
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="oldalex-mini-card h-100">
                <h3 className="h6 fw-bold">AI Innovation</h3>
                <p className="text-muted mb-0">
                  Building AI-driven products that reshape operations,
                  decisioning, and customer experiences across industries.
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="oldalex-mini-card h-100">
                <h3 className="h6 fw-bold">SaaS Development</h3>
                <p className="text-muted mb-0">
                  Building operational SaaS including TaxiOps, a connected
                  platform for driver onboarding, dispatch, live fleet
                  visibility, mobile trip execution, fares, receipts,
                  reporting, and AI-powered operations intelligence.
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="oldalex-mini-card h-100">
                <h3 className="h6 fw-bold">Content &amp; Media Ventures</h3>
                <p className="text-muted mb-0">
                  Building After Hours Audio: a faceless, ambient channel for
                  focus and calm, and expanding narrator-led media without
                  on-camera talent.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container pb-5">
        <div className="oldalex-card oldalex-venture-card">
          <div className="row g-4 align-items-center">
            <div className="col-lg-7">
              <p className="eyebrow mb-2">Featured Venture</p>
              <h2 className="h4 fw-bold mb-2">TaxiOps</h2>
              <p className="text-muted mb-3">
                TaxiOps is an official Old Alex Hub venture and public marketing
                website for a SaaS operations platform built for taxi and
                Non-Emergency Medical Transportation providers.
              </p>
              <p className="text-muted mb-3">
                The platform connects driver onboarding, dispatch operations,
                live fleet visibility, mobile trip execution, fares, receipts,
                reporting, and AI-powered Operations Intelligence in one
                workflow built from inside the transportation business.
              </p>
              <div className="oldalex-venture-actions">
                <a
                  href="https://taxi-ops.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-primary btn-sm"
                >
                  Visit TaxiOps
                </a>
              </div>
            </div>

            <div className="col-lg-5">
              <div className="oldalex-venture-panel">
                <div className="oldalex-venture-kicker">taxi-ops.com</div>
                <h3 className="h5 fw-bold mb-3">
                  Classic dispatch. Modern operations. AI-powered intelligence.
                </h3>
                <div className="oldalex-venture-badges">
                  <span>Phase One: Denver, CO</span>
                  <span>Taxi + NEMT</span>
                </div>
                <ul className="text-muted small mb-0 ps-3">
                  <li>Built for taxi and Non-Emergency Medical Transportation providers.</li>
                  <li>No new enrollments while Phase One capacity is active.</li>
                  <li>Real product screenshots from the live TaxiOps platform.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container pb-5">
        <div className="row g-4 align-items-center">
          <div className="col-lg-7">
            <div className="oldalex-card h-100">
              <p className="eyebrow mb-2">Content &amp; Media</p>
              <h2 className="h5 fw-bold mb-2">After Hours Audio</h2>
              <div className="text-muted mb-3">
                <p className="mb-2">
                  After Hours Audio is a late-night music channel built for
                  quiet rooms, low lights, and long hours after the world slows
                  down.
                </p>
                <p className="mb-2">
                  Ambient, instrumental, and restrained guitar pieces designed
                  to sit beside you without pulling focus. AI-assisted,
                  carefully curated, and edited for a consistent midnight mood.
                  New uploads land regularly. Headphones recommended.
                </p>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-3">
                <img
                  src={YtLogo}
                  alt="After Hours Audio logo"
                  className="logo-yt logo-yt-sm"
                />
                <a
                  href="https://www.youtube.com/@AfterHoursAudio-c4z"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  Visit channel
                </a>
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="oldalex-card h-100">
              <p className="eyebrow mb-2">Portfolio at a glance</p>
              <ul className="text-muted small mb-0 ps-3">
                <li>
                  Balancing tech-focused investing, operating business
                  ownership, and in-house venture building.
                </li>
                <li>
                  Combining technology stocks, mobility operations, real assets,
                  and AI products for resilience.
                </li>
                <li>
                  Media properties to extend reach and validate audience demand.
                </li>
                <li>
                  Operational businesses include ownership of Trans Voyage Taxi
                  in Denver, Colorado.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {showAppSection && (
        <section className="container pb-5">
          <div className="oldalex-card">
            <p className="eyebrow mb-2">Mobile Products</p>
            <h2 className="h4 fw-bold mb-1">Android App Library</h2>
            <p className="text-muted mb-4">
              A collection of published Android applications built and shipped under Old Alex Hub LLC.
            </p>

            {appsError && <div className="alert alert-warning py-2 mb-4">{appsError}</div>}

            {appsLoading ? (
              <div className="oldalex-apps-grid">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="oldalex-app-card is-skeleton" aria-hidden="true">
                    <div className="oldalex-app-header">
                      <div className="oldalex-app-icon oldalex-skeleton-block" />
                      <div className="oldalex-app-title-block">
                        <div className="oldalex-skeleton-block oldalex-skeleton-line" />
                        <div className="oldalex-skeleton-block oldalex-skeleton-line w-60" />
                      </div>
                    </div>
                    <div className="oldalex-skeleton-block oldalex-skeleton-line w-100" />
                    <div className="oldalex-skeleton-block oldalex-skeleton-line w-75" />
                  </div>
                ))}
              </div>
            ) : publishedApps.length > 0 ? (
              <>
                <div className="oldalex-apps-toolbar">
                  <label className="oldalex-apps-search">
                    <Search size={16} />
                    <input
                      type="search"
                      value={appSearch}
                      onChange={(e) => setAppSearch(e.target.value)}
                      placeholder="Search apps by name or category"
                      aria-label="Search Android apps"
                    />
                  </label>
                  <label className="oldalex-apps-sort">
                    <SlidersHorizontal size={16} />
                    <select
                      value={appSort}
                      onChange={(e) => setAppSort(e.target.value)}
                      aria-label="Sort Android apps"
                    >
                      <option value="newest">Newest</option>
                      <option value="az">A to Z</option>
                      <option value="rating">Top rated</option>
                    </select>
                  </label>
                </div>

                {appCategoryOptions.length > 2 && (
                  <div className="oldalex-apps-filters" aria-label="App categories">
                    {appCategoryOptions.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        className={`oldalex-apps-filter-chip ${appCategory === cat ? "active" : ""}`}
                        onClick={() => setAppCategory(cat)}
                      >
                        <span>{cat}</span>
                        <span className="oldalex-apps-filter-count">{appCategoryCounts[cat] || 0}</span>
                      </button>
                    ))}
                  </div>
                )}

                <div className="oldalex-apps-summary">
                  <span>
                    {filteredApps.length > 0
                      ? `Showing ${filteredApps.length} of ${publishedApps.length} apps`
                      : "No apps match your filters"}
                  </span>
                  {appFiltersActive && (
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={clearAppFilters}>
                      Clear filters
                    </button>
                  )}
                </div>

                {filteredApps.length > 0 ? (
                  <div className="oldalex-apps-grid">
                    {filteredApps.map((app) => (
                      <div key={app._id} className="oldalex-app-card">
                        <div className="oldalex-app-header">
                          {app.iconUrl ? (
                            <img
                              src={getDirectImageUrl(app.iconUrl)}
                              alt=""
                              className="oldalex-app-icon"
                            />
                          ) : (
                            <div className="oldalex-app-icon oldalex-app-icon--placeholder">
                              <Smartphone size={18} />
                            </div>
                          )}
                          <div className="oldalex-app-title-block">
                            <h3 className="h6 fw-bold mb-0">{app.name}</h3>
                            {app.category && (
                              <span className="oldalex-app-category">{app.category}</span>
                            )}
                          </div>
                          <span className={`oldalex-app-status oldalex-app-status--${app.status}`}>
                            {STATUS_LABEL[app.status] || app.status}
                          </span>
                        </div>

                        {renderAppRating(app.rating)}

                        {app.description && (
                          <p className="text-muted small mb-2">{truncateAppText(app.description)}</p>
                        )}

                        <div className="oldalex-app-links">
                          {app.playStoreLink && (
                            <a
                              href={app.playStoreLink}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm btn-primary"
                            >
                              Get it on Google Play
                            </a>
                          )}
                          {app.appStoreLink && (
                            <a
                              href={app.appStoreLink}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm btn-outline-dark"
                            >
                              Download on the App Store
                            </a>
                          )}
                          {app.githubLink && (
                            <a
                              href={app.githubLink}
                              target="_blank"
                              rel="noreferrer"
                              className="oldalex-app-github"
                              aria-label={`${app.name} on GitHub`}
                              title="View on GitHub"
                            >
                              <Github size={16} />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="oldalex-apps-empty">
                    <p className="text-muted mb-2">No apps match your current search or filters.</p>
                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={clearAppFilters}>
                      Clear filters
                    </button>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </section>
      )}

      <section className="container pb-5">
        <div className="oldalex-card">
          <h2 className="h4 fw-bold mb-3">Founder's Message</h2>
          <p className="text-muted">
            As the founder of Old Alex Hub LLC, I am proud to share the vision
            and values that drive our company. When I established this firm, my
            goal was to create more than just a holding company - I set out
            to build a hub for ventures and investments that could span
            traditional markets and frontier technologies alike. My experiences
            in finance and
            technology have taught me that true innovation happens when we
            connect disciplined investing and business building with bold new
            ideas.
          </p>
          <p className="text-muted">
            Ownership comes with responsibility, and I take that to heart. At
            Old Alex Hub, we value integrity, diligence, and foresight in
            everything we do. Whether we are managing technology-focused stock
            positions, operating a portfolio of businesses,
            developing a SaaS application, or producing content for After Hours
            Audio, our team operates with professionalism and a long-term
            perspective. We are here to deliver results for our stakeholders -
            be they investors, partners, clients, or audience members - and to
            do so in a
            way that upholds trust and transparency.
          </p>
          <p className="text-muted">
            Looking ahead, I am excited about the future of Old Alex Hub LLC and
            the ventures under our umbrella. We live in an era of rapid change
            and immense opportunity. By staying true to our mission and
            fostering a culture of continuous improvement and innovation, I
            believe we will continue turning ambitious ideas into impactful
            realities. Thank you for taking the time to learn about our company.
            I invite you to join us on this journey - as a client, partner, or
            supporter - as we build lasting value and strive to make a
            meaningful mark across all our areas of expertise.
          </p>
          <p className="text-muted mb-0">
            <span className="d-block">Sincerely,</span>
            <span className="fw-semibold">Mohamed Gad</span>
            <span className="d-block">
              Founder &amp; Owner, Old Alex Hub LLC
            </span>
          </p>
        </div>
      </section>
    </div>
  );
};

export default OldAlexHub;
